import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { actividadService } from '../../services/actividadService';
import { actividadAdminService, DiocesisJADGroup, ProvinciaJANGroup } from '../../services/actividadAdminService';
import { ActividadModal } from '../ActividadModal';
import { AdminDetalleActividadJAModal } from './AdminDetalleActividadJAModal';
import { ActividadEconomica } from '../../lib/supabase';
import { OwnerContext } from '../../types/actividad';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { useToggleSet } from '../../hooks/useToggleSet';

interface ActividadesJAViewProps {
  readonly ownerContext: OwnerContext;
  readonly readOnly: boolean;
}

// ---------------------------------------------------------------------------
// Helpers de badge de estado
// ---------------------------------------------------------------------------

function getEstadoBadgeClass(estado: string): string {
  switch (estado) {
    case 'planificacion': return 'bg-gray-100 text-gray-600';
    case 'en_curso':      return 'bg-blue-100 text-blue-700';
    case 'finalizada':    return 'bg-green-100 text-green-700';
    case 'archivada':     return 'bg-red-100 text-red-600';
    default:              return 'bg-gray-100 text-gray-600';
  }
}

function getEstadoLabel(estado: string): string {
  switch (estado) {
    case 'planificacion': return 'Planificación';
    case 'en_curso':      return 'En Curso';
    case 'finalizada':    return 'Finalizada';
    case 'archivada':     return 'Archivada';
    default:              return estado;
  }
}

// ---------------------------------------------------------------------------
// Skeleton de carga
// ---------------------------------------------------------------------------

const SkeletonRow: React.FC = () => (
  <tr className="border-t border-gray-100 animate-pulse" aria-hidden="true" tabIndex={-1}>
    <td className="py-3 pl-4 pr-2"><div className="h-4 bg-gray-200 rounded w-48" /></td>
    <td className="py-3 px-2"><div className="h-5 bg-gray-200 rounded w-20 mx-auto" /></td>
    <td className="py-3 px-2"><div className="h-4 bg-gray-200 rounded w-24 mx-auto" /></td>
    <td className="py-3 px-2"><div className="h-4 bg-gray-200 rounded w-20 mx-auto" /></td>
    <td className="py-3 px-2"><div className="h-4 bg-gray-200 rounded w-20 mx-auto" /></td>
    <td className="py-3 pl-2 pr-4"><div className="h-4 bg-gray-200 rounded w-20 mx-auto" /></td>
  </tr>
);

// ---------------------------------------------------------------------------
// Fila de actividad unificada
// ---------------------------------------------------------------------------

interface ActividadTableRowProps {
  readonly actividad: ActividadEconomica;
  readonly onClick: () => void;
  readonly indent?: 'normal' | 'deep';
}

const ActividadTableRow: React.FC<ActividadTableRowProps> = ({ actividad, onClick, indent = 'normal' }) => {
  const utilidad = Number(actividad.total_ingresos || 0) - Number(actividad.total_costos || 0);
  const firstCellPadding = indent === 'deep' ? 'pl-8' : 'pl-4';
  return (
    <tr
      className="border-t border-gray-100 hover:bg-blue-50 transition-colors cursor-pointer group"
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
    >
      <td className={`py-3 ${firstCellPadding} pr-2`}>
        <p className="font-medium text-gray-800 text-sm group-hover:text-blue-700 transition-colors">
          {actividad.nombre}
        </p>
      </td>
      <td className="py-3 px-2 text-center">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getEstadoBadgeClass(actividad.estado)}`}>
          {getEstadoLabel(actividad.estado)}
        </span>
      </td>
      <td className="py-3 px-2 text-center text-sm text-gray-500">
        {actividad.fecha_inicio ? formatDate(actividad.fecha_inicio) : '—'}
      </td>
      <td className="py-3 px-2 text-center text-sm text-emerald-600 font-medium">
        {formatCurrency(Number(actividad.total_ingresos || 0))}
      </td>
      <td className="py-3 px-2 text-center text-sm text-red-500 font-medium">
        {formatCurrency(Number(actividad.total_costos || 0))}
      </td>
      <td className={`py-3 pl-2 pr-4 text-center text-sm font-bold ${utilidad >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
        {formatCurrency(utilidad)}
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Vista editable (readOnly=false)
// ---------------------------------------------------------------------------

interface EditableViewProps {
  readonly ownerContext: OwnerContext;
}

const EditableView: React.FC<EditableViewProps> = ({ ownerContext }) => {
  const navigate = useNavigate();
  const [actividades, setActividades] = useState<ActividadEconomica[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const cargar = useCallback(async () => {
    try {
      setLoading(true);
      const data = ownerContext.tipo === 'diocesis'
        ? await actividadService.getByDiocesis(ownerContext.id)
        : await actividadService.getByProvincia(ownerContext.id);
      setActividades(data);
    } catch {
      // error silencioso — la UI mostrará lista vacía
    } finally {
      setLoading(false);
    }
  }, [ownerContext]);

  useEffect(() => { void cargar(); }, [cargar]);

  const handleRowClick = (actividad: ActividadEconomica) => {
    navigate(`/admin/actividades/${actividad.id}?owner=${ownerContext.tipo}&ownerId=${ownerContext.id}`);
  };

  const entidadLabel = ownerContext.tipo === 'diocesis' ? 'JAD' : 'JAN';

  return (
    <div className="space-y-4">
      {/* Header con botón */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Actividades económicas de la {entidadLabel}
        </p>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Nueva Actividad
        </button>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="py-2 pl-4 pr-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Nombre</th>
              <th className="py-2 px-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
              <th className="py-2 px-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha inicio</th>
              <th className="py-2 px-2 text-center text-xs font-semibold text-emerald-500 uppercase tracking-wide">Ingresos</th>
              <th className="py-2 px-2 text-center text-xs font-semibold text-red-400 uppercase tracking-wide">Costos</th>
              <th className="py-2 pl-2 pr-4 text-center text-xs font-semibold text-blue-500 uppercase tracking-wide">Utilidad</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            )}
            {!loading && actividades.length === 0 && (
              <tr>
                <td colSpan={6} className="py-16 text-center">
                  <Briefcase className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium text-gray-500">
                    La {entidadLabel} aún no tiene actividades registradas
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowModal(true)}
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Nueva Actividad
                  </button>
                </td>
              </tr>
            )}
            {!loading && actividades.map(act => (
              <ActividadTableRow
                key={act.id}
                actividad={act}
                onClick={() => handleRowClick(act)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal de creación */}
      {showModal && (
        <ActividadModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); void cargar(); }}
          ownerContext={ownerContext}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Grupos expandibles compartidos (JAD y JAN)
// ---------------------------------------------------------------------------

interface GrupoItem {
  readonly id: string;
  readonly nombre: string;
  readonly subLabel?: string;
  readonly totalActividades: number;
  readonly totalIngresos: number;
  readonly totalCostos: number;
  readonly utilidadNeta: number;
  readonly actividades: ActividadEconomica[];
  readonly ownerContext: OwnerContext;
}

interface SelectedJA {
  readonly actividadId: string;
  readonly ownerContext: OwnerContext;
}

interface GruposExpandiblesViewProps {
  readonly grupos: GrupoItem[];
  readonly loading: boolean;
  readonly emptyLabel: string;
  readonly selected: SelectedJA | null;
  readonly onSelect: (actividadId: string, ctx: OwnerContext) => void;
  readonly onCloseModal: () => void;
}

const GruposExpandiblesView: React.FC<GruposExpandiblesViewProps> = ({
  grupos, loading, emptyLabel, selected, onSelect, onCloseModal,
}) => {
  const { expanded, toggle } = useToggleSet();

  return (
    <div className="space-y-2">
      {loading && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <tbody>
              <SkeletonRow />
              <SkeletonRow />
              <SkeletonRow />
            </tbody>
          </table>
        </div>
      )}

      {!loading && grupos.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm py-16 text-center">
          <Briefcase className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-gray-500">{emptyLabel}</p>
        </div>
      )}

      {!loading && grupos.map(grupo => {
        const isOpen = expanded.has(grupo.id);
        return (
          <div key={grupo.id} className="border border-gray-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => toggle(grupo.id)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
            >
              <div className="flex items-center gap-2 min-w-0">
                {isOpen
                  ? <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  : <ChevronRight className="w-4 h-4 text-gray-500 flex-shrink-0" />
                }
                <div className="min-w-0">
                  {grupo.subLabel !== undefined && (
                    <p className="text-xs text-gray-400">{grupo.subLabel}</p>
                  )}
                  <p className="font-bold text-gray-800 truncate">{grupo.nombre}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 flex-shrink-0 text-sm">
                <span className="text-gray-500">{grupo.totalActividades} act.</span>
                <span className="text-emerald-600 font-medium">{formatCurrency(grupo.totalIngresos)}</span>
                <span className="text-red-500 font-medium">{formatCurrency(grupo.totalCostos)}</span>
                <span className={`font-bold ${grupo.utilidadNeta >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {formatCurrency(grupo.utilidadNeta)}
                </span>
              </div>
            </button>

            {isOpen && (
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50/50 border-t border-gray-100">
                    <th className="py-2 pl-8 pr-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wide">Nombre</th>
                    <th className="py-2 px-2 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">Estado</th>
                    <th className="py-2 px-2 text-center text-xs font-semibold text-gray-400 uppercase tracking-wide">Fecha inicio</th>
                    <th className="py-2 px-2 text-center text-xs font-semibold text-emerald-400 uppercase tracking-wide">Ingresos</th>
                    <th className="py-2 px-2 text-center text-xs font-semibold text-red-400 uppercase tracking-wide">Costos</th>
                    <th className="py-2 pl-2 pr-4 text-center text-xs font-semibold text-blue-400 uppercase tracking-wide">Utilidad</th>
                  </tr>
                </thead>
                <tbody>
                  {grupo.actividades.length === 0 && (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-sm text-gray-400">
                        No hay actividades que coincidan
                      </td>
                    </tr>
                  )}
                  {grupo.actividades.map(act => (
                    <ActividadTableRow
                      key={act.id}
                      actividad={act}
                      onClick={() => onSelect(act.id, grupo.ownerContext)}
                      indent="deep"
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

      <AdminDetalleActividadJAModal
        isOpen={!!selected}
        actividadId={selected?.actividadId ?? ''}
        ownerContext={selected?.ownerContext ?? { tipo: 'diocesis', id: '', nombre: '' }}
        onClose={onCloseModal}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Vista de solo lectura — JADs agrupadas por diócesis
// ---------------------------------------------------------------------------

interface JADGroupedViewProps {
  readonly ownerContext: OwnerContext;
}

const JADGroupedView: React.FC<JADGroupedViewProps> = ({ ownerContext }) => {
  const [groups, setGroups] = useState<DiocesisJADGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SelectedJA | null>(null);

  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        const filtro = ownerContext.tipo === 'provincia'
          ? { provinciaEclesiasticaId: ownerContext.id }
          : undefined;
        const data = await actividadAdminService.getActividadesJADGrouped(filtro);
        setGroups(data);
      } catch {
        // error silencioso
      } finally {
        setLoading(false);
      }
    };
    void cargar();
  }, [ownerContext]);

  const grupos: GrupoItem[] = groups.map(g => ({
    id: g.diocesisId,
    nombre: g.diocesisNombre,
    subLabel: g.diocesisTipo === 'diocesis' ? 'Diócesis' : 'Prefectura',
    totalActividades: g.totalActividades,
    totalIngresos: g.totalIngresos,
    totalCostos: g.totalCostos,
    utilidadNeta: g.utilidadNeta,
    actividades: g.actividades,
    ownerContext: { tipo: 'diocesis', id: g.diocesisId, nombre: g.diocesisNombre },
  }));

  return (
    <GruposExpandiblesView
      grupos={grupos}
      loading={loading}
      emptyLabel="No hay actividades JAD registradas"
      selected={selected}
      onSelect={(id, ctx) => setSelected({ actividadId: id, ownerContext: ctx })}
      onCloseModal={() => setSelected(null)}
    />
  );
};

// ---------------------------------------------------------------------------
// Vista de solo lectura — JANs agrupadas por provincia
// ---------------------------------------------------------------------------

const JANGroupedView: React.FC = () => {
  const [groups, setGroups] = useState<ProvinciaJANGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SelectedJA | null>(null);

  useEffect(() => {
    const cargar = async () => {
      try {
        setLoading(true);
        const data = await actividadAdminService.getActividadesJANGrouped();
        setGroups(data);
      } catch {
        // error silencioso
      } finally {
        setLoading(false);
      }
    };
    void cargar();
  }, []);

  const grupos: GrupoItem[] = groups.map(g => ({
    id: g.provinciaId,
    nombre: g.provinciaNombre,
    totalActividades: g.totalActividades,
    totalIngresos: g.totalIngresos,
    totalCostos: g.totalCostos,
    utilidadNeta: g.utilidadNeta,
    actividades: g.actividades,
    ownerContext: { tipo: 'provincia', id: g.provinciaId, nombre: g.provinciaNombre },
  }));

  return (
    <GruposExpandiblesView
      grupos={grupos}
      loading={loading}
      emptyLabel="No hay actividades JAN registradas"
      selected={selected}
      onSelect={(id, ctx) => setSelected({ actividadId: id, ownerContext: ctx })}
      onCloseModal={() => setSelected(null)}
    />
  );
};

// ---------------------------------------------------------------------------
// Componente raíz
// ---------------------------------------------------------------------------

export const ActividadesJAView: React.FC<ActividadesJAViewProps> = ({ ownerContext, readOnly }) => {
  if (!readOnly) {
    return <EditableView ownerContext={ownerContext} />;
  }

  // readOnly=true: jerarquía colapsable por tipo de entidad
  if (ownerContext.tipo === 'provincia') {
    return <JADGroupedView ownerContext={ownerContext} />;
  }

  // Fallback: mostrar JANs (tipo 'global' u otro)
  return <JANGroupedView />;
};
